// eslint-disable-next-line jira/import-whitelist
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import {
	JIRA_PROJECT_STATUS_ARCHIVED,
	TEAM_MANAGED_PROJECT,
	convertToProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { getProjectIdOrKey } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
import type { ProjectContext } from '../types.tsx';
import { QUERY } from './gql.tsx';
import type { ProjectResponse } from './types.tsx';
import { getIsInProjectAccessRouteWhitelist } from './utils.tsx';

export const AGG_NO_PROJECT_DATA = 512;

const getProjectContextData = async (
	projectIdOrKey: string,
	cloudId: string,
	shouldFetchProjectAccess?: Boolean, // Clean up with `split_share_button_for_tmp_admins experiment`
): Promise<ProjectContext> => {
	const fetchStartTime = performance.now();

	const { data, errors } = await performPostRequest<ProjectResponse>('/gateway/api/graphql', {
		method: 'POST',
		headers: {
			'content-type': 'application/json',
			'X-ExperimentalApi': 'JiraProject, projectStyle',
		},
		body: JSON.stringify({
			query: QUERY,
			variables: {
				cloudId,
				projectIdOrKey,
				shouldFetchProjectAccess: !!shouldFetchProjectAccess,
			},
		}),
	});

	addSpanToAll(
		'fetch',
		'fetchProjectByKeyOrId',
		[{ name: 'network' }],
		fetchStartTime,
		performance.now(),
	);

	if (errors != null && errors.length > 0) {
		const statusCode = errors[0].extensions?.statusCode;
		throw new FetchError(
			statusCode,
			`Fetch project context via AGG failed. Errors: ${errors.map((err) => err.message).join(', ')}`,
		);
	}
	const projectData = data?.jira_projectByIdOrKey;
	if (!projectData) {
		throw new FetchError(
			AGG_NO_PROJECT_DATA,
			'Fetch project context via AGG succeeded but no data received',
		);
	}
	const {
		projectId,
		key,
		uuid,
		name,
		avatar,
		projectType,
		projectStyle,
		status,
		created,
		isAIEnabled,
		canAdministerProject,
		accessLevel,
	} = projectData;

	return {
		projectKey: key,
		projectId: +projectId,
		projectUuid: uuid,
		projectName: name,
		avatarUrl: avatar?.medium ?? '',
		simplified: projectStyle === TEAM_MANAGED_PROJECT,
		projectType: convertToProjectType(projectType),
		isProjectAdmin: canAdministerProject?.canPerform ?? false,
		isProjectArchived: status === JIRA_PROJECT_STATUS_ARCHIVED,
		created,
		isAIEnabled,
		accessLevel,
	};
};

/**
 * Determines if the current user is an admin of the specified project by extracting project identifiers from a route, verifying these identifiers, and concurrently fetching project context and user permissions. It handles errors gracefully by throwing exceptions if data retrieval fails.
 */

const getData = async (
	matchedRoute: RouterDataContext,
	{ tenantContext: { cloudId } }: ResourceStoreContext,
): Promise<ProjectContext | null> => {
	const projectIdOrKey = getProjectIdOrKey(matchedRoute.match, matchedRoute.query);
	if (projectIdOrKey == null || projectIdOrKey === '') {
		return null;
	}

	const shouldFetchProjectAccess = getIsInProjectAccessRouteWhitelist(matchedRoute);
	return getProjectContextData(projectIdOrKey, cloudId, shouldFetchProjectAccess);
};

export default getData;
